/**
 * CRM Page - HubSpot-style pipeline management
 *
 * Features:
 * - Pipeline view with stages (lead/qualified/proposal/negotiation/customer)
 * - Contact cards with deal value and owner
 * - Drag-and-drop style stage transitions
 * - Contact detail view
 */

import { useState, FormEvent } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import {
  getContactsByWorkspace,
  createContact,
  updateContactStage,
  deleteContact,
  getCrmSummary,
  getPipelineDealValue,
} from '../lib/services/crm.service';
import { getTeamMembersByWorkspace, getTeamMemberById } from '../lib/services/team.service';
import {
  PlusIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  XMarkIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { format, formatDistanceToNow } from 'date-fns';
import clsx from 'clsx';
import { CONTACT_STAGE_CONFIG, type Contact, type ContactStage } from '../lib/types';

const PIPELINE_STAGES: ContactStage[] = ['lead', 'qualified', 'proposal', 'negotiation', 'customer'];

export function CRMPage() {
  const { currentWorkspace, refreshWorkspaces } = useWorkspace();
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [draggedContactId, setDraggedContactId] = useState<string | null>(null);

  if (!currentWorkspace) return null;

  const contacts = getContactsByWorkspace(currentWorkspace.id);
  const crmSummary = getCrmSummary(currentWorkspace.id);
  const pipelineDealValue = getPipelineDealValue(currentWorkspace.id);
  const teamMembers = getTeamMembersByWorkspace(currentWorkspace.id);

  const contactsByStage = PIPELINE_STAGES.reduce((acc, stage) => {
    acc[stage] = contacts.filter((c) => c.stage === stage);
    return acc;
  }, {} as Record<ContactStage, Contact[]>);

  const handleMoveContact = (contactId: string, newStage: ContactStage) => {
    updateContactStage(contactId, newStage);
    refreshWorkspaces();
  };

  const handleCreateContact = (data: {
    name: string;
    email: string;
    company: string;
    dealValue: number;
    ownerId?: string;
  }) => {
    createContact({
      workspaceId: currentWorkspace.id,
      ...data,
      stage: 'lead',
    });
    setShowAddForm(false);
    refreshWorkspaces();
  };

  const onDragStart = (e: React.DragEvent, contactId: string) => {
    setDraggedContactId(contactId);
    e.dataTransfer.setData('contactId', contactId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDrop = (e: React.DragEvent, newStage: ContactStage) => {
    e.preventDefault();
    const contactId = e.dataTransfer.getData('contactId') || draggedContactId;
    if (contactId) {
      handleMoveContact(contactId, newStage);
    }
    setDraggedContactId(null);
  };

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col">
      {/* Header with KPIs */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Sales Pipeline</h1>
            <p className="text-sm text-gray-500">
              {crmSummary.total} contacts · ₩{crmSummary.totalDealValue.toLocaleString()} total pipeline
            </p>
          </div>
          <button onClick={() => setShowAddForm(true)} className="btn-primary btn-sm">
            <PlusIcon className="h-4 w-4" />
            Add Contact
          </button>
        </div>

        {/* Pipeline value summary */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {PIPELINE_STAGES.map((stage) => {
            const config = CONTACT_STAGE_CONFIG[stage];
            const value = pipelineDealValue[stage];
            const count = contactsByStage[stage].length;

            return (
              <div
                key={stage}
                className={clsx(
                  'flex-shrink-0 px-4 py-2 rounded-lg transition-all',
                  `bg-${config.color}-50 border border-${config.color}-100`
                )}
                style={{
                  backgroundColor: `var(--${config.color}-50, #f8fafc)`,
                  borderColor: `var(--${config.color}-100, #e2e8f0)`,
                }}
              >
                <p className="text-xs font-medium text-gray-600">{config.label}</p>
                <p className="text-sm font-semibold text-gray-900">₩{value.toLocaleString()}</p>
                <p className="text-xs text-gray-500">{count} contacts</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pipeline Board */}
      <div className="flex-1 overflow-x-auto p-6 bg-gray-50/50">
        <div className="flex gap-4 min-w-max h-full">
          {PIPELINE_STAGES.map((stage) => {
            const config = CONTACT_STAGE_CONFIG[stage];
            const stageContacts = contactsByStage[stage];
            const stageValue = pipelineDealValue[stage];

            return (
              <div
                key={stage}
                className="w-80 flex flex-col"
                onDragOver={onDragOver}
                onDrop={(e) => onDrop(e, stage)}
              >
                {/* Column header */}
                <div className="pipeline-header">
                  <div className="pipeline-header-title">
                    <span className={`status-dot status-dot-${config.color}`} />
                    {config.label}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 font-medium">₩{(stageValue / 1000).toFixed(0)}K</span>
                    <span className="pipeline-header-count">{stageContacts.length}</span>
                  </div>
                </div>

                {/* Column content */}
                <div className={clsx(
                  'pipeline-column flex-1 overflow-y-auto transition-colors duration-200 rounded-b-xl border-x border-b border-gray-100',
                  draggedContactId && 'bg-gray-100/50 border-dashed border-gray-300'
                )}>
                  {stageContacts.map((contact) => (
                    <ContactCard
                      key={contact.id}
                      contact={contact}
                      isDragging={draggedContactId === contact.id}
                      onDragStart={(e) => onDragStart(e, contact.id)}
                      onSelect={() => setSelectedContact(contact)}
                      onDelete={() => {
                        if (confirm(`Delete ${contact.name}?`)) {
                          deleteContact(contact.id);
                          refreshWorkspaces();
                        }
                      }}
                      onMoveForward={stage !== 'customer' ? () => {
                        const nextIndex = PIPELINE_STAGES.indexOf(stage) + 1;
                        if (nextIndex < PIPELINE_STAGES.length) {
                          handleMoveContact(contact.id, PIPELINE_STAGES[nextIndex]);
                        }
                      } : undefined}
                      onMoveBackward={stage !== 'lead' ? () => {
                        const prevIndex = PIPELINE_STAGES.indexOf(stage) - 1;
                        if (prevIndex >= 0) {
                          handleMoveContact(contact.id, PIPELINE_STAGES[prevIndex]);
                        }
                      } : undefined}
                    />
                  ))}
                  {stageContacts.length === 0 && (
                    <div className="text-center py-12 text-sm text-gray-400 italic">
                      Drop here
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Contact Modal */}
      {showAddForm && (
        <AddContactModal
          teamMembers={teamMembers}
          onClose={() => setShowAddForm(false)}
          onSubmit={handleCreateContact}
        />
      )}

      {/* Contact Detail Sidebar */}
      {selectedContact && (
        <ContactDetailSidebar
          contact={selectedContact}
          onClose={() => setSelectedContact(null)}
          onUpdate={refreshWorkspaces}
        />
      )}
    </div>
  );
}

function ContactCard({
  contact,
  isDragging,
  onDragStart,
  onSelect,
  onDelete,
  onMoveForward,
  onMoveBackward,
}: {
  contact: Contact;
  isDragging?: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onSelect: () => void;
  onDelete: () => void;
  onMoveForward?: () => void;
  onMoveBackward?: () => void;
}) {
  const owner = contact.ownerId ? getTeamMemberById(contact.ownerId) : null;

  return (
    <div
      draggable
      onDragStart={onDragStart}
      className={clsx(
        'pipeline-item group cursor-grab active:cursor-grabbing transition-all duration-200',
        isDragging && 'opacity-40 scale-95 border-primary-300'
      )}
      onClick={onSelect}
    >
      <div className="flex items-start gap-3">
        <div className="avatar avatar-sm bg-primary-100 text-primary-700 font-bold flex-shrink-0">
          {contact.name.charAt(0)}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-gray-900 truncate group-hover:text-primary-600 transition-colors">
            {contact.name}
          </h3>
          {contact.company && (
            <p className="text-xs text-gray-500 truncate flex items-center gap-1 mt-0.5">
              <BuildingOfficeIcon className="h-3 w-3" />
              {contact.company}
            </p>
          )}
        </div>
      </div>

      {contact.dealValue && contact.dealValue > 0 && (
        <div className="mt-2.5 flex items-center gap-1 text-sm font-bold text-green-600 bg-green-50/50 w-fit px-2 py-0.5 rounded border border-green-100/50">
          <CurrencyDollarIcon className="h-4 w-4" />
          ₩{contact.dealValue.toLocaleString()}
        </div>
      )}

      <div className="flex items-center justify-between mt-3.5">
        <div className="flex items-center gap-2 text-[11px] text-gray-400 font-medium">
          {contact.lastContactedAt && (
            <span className="flex items-center gap-1">
              {formatDistanceToNow(new Date(contact.lastContactedAt), { addSuffix: true })}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {/* Move buttons - kept for accessibility/mobile */}
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            {onMoveBackward && (
              <button
                onClick={(e) => { e.stopPropagation(); onMoveBackward(); }}
                className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                title="Move back"
              >
                <ChevronLeftIcon className="h-3.5 w-3.5" />
              </button>
            )}
            {onMoveForward && (
              <button
                onClick={(e) => { e.stopPropagation(); onMoveForward(); }}
                className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                title="Move forward"
              >
                <ChevronRightIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
            title="Delete"
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>

          {/* Owner */}
          {owner ? (
            <div className="avatar avatar-xs ring-2 ring-white" title={`Owner: ${owner.name}`}>
              {owner.name.charAt(0)}
            </div>
          ) : (
            <div className="avatar avatar-xs bg-gray-50 text-gray-300 ring-2 ring-white">
              <UserIcon className="h-3 w-3" />
            </div>
          )}
        </div>
      </div>

      {/* Tags */}
      {contact.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2.5 pt-2 border-t border-gray-50">
          {contact.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-gray-50 text-gray-500 rounded border border-gray-100 font-medium">
              {tag}
            </span>
          ))}
          {contact.tags.length > 2 && (
            <span className="text-[10px] text-gray-400 ml-1">+{contact.tags.length - 2}</span>
          )}
        </div>
      )}
    </div>
  );
}

function AddContactModal({
  teamMembers,
  onClose,
  onSubmit,
}: {
  teamMembers: ReturnType<typeof getTeamMembersByWorkspace>;
  onClose: () => void;
  onSubmit: (data: { name: string; email: string; company: string; dealValue: number; ownerId?: string }) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [dealValue, setDealValue] = useState('');
  const [ownerId, setOwnerId] = useState('');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      email: email.trim(),
      company: company.trim(),
      dealValue: parseInt(dealValue) || 0,
      ownerId: ownerId || undefined,
    });
  };

  return (
    <div className="modal">
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-content animate-slide-in">
        <div className="modal-header">
          <h2 className="text-lg font-semibold text-gray-900">Add New Contact</h2>
          <button onClick={onClose} className="btn-icon">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body space-y-4">
            <div>
              <label className="label">Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="Contact name"
                autoFocus
              />
            </div>
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="email@example.com"
              />
            </div>
            <div>
              <label className="label">Company</label>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="input"
                placeholder="Company name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Deal Value (₩)</label>
                <input
                  type="number"
                  value={dealValue}
                  onChange={(e) => setDealValue(e.target.value)}
                  className="input"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="label">Owner</label>
                <select
                  value={ownerId}
                  onChange={(e) => setOwnerId(e.target.value)}
                  className="select"
                >
                  <option value="">Unassigned</option>
                  {teamMembers.map((member) => (
                    <option key={member.id} value={member.id}>{member.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={!name.trim()}>
              Add Contact
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ContactDetailSidebar({
  contact,
  onClose,
  onUpdate,
}: {
  contact: Contact;
  onClose: () => void;
  onUpdate: () => void;
}) {
  const stageConfig = CONTACT_STAGE_CONFIG[contact.stage];
  const owner = contact.ownerId ? getTeamMemberById(contact.ownerId) : null;

  const handleStageChange = (newStage: ContactStage) => {
    updateContactStage(contact.id, newStage);
    onUpdate();
  };

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-white border-l border-gray-200 shadow-lg z-50 animate-slide-in-right">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <span className={clsx('badge', `badge-${stageConfig.color === 'green' ? 'success' : stageConfig.color === 'red' ? 'danger' : 'info'}`)}>
          {stageConfig.label}
        </span>
        <button onClick={onClose} className="btn-icon">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="p-4 space-y-6 overflow-y-auto h-[calc(100vh-4rem)]">
        {/* Contact header */}
        <div className="flex items-start gap-4">
          <div className="avatar avatar-lg bg-primary-100 text-primary-700">
            {contact.name.charAt(0)}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{contact.name}</h2>
            {contact.role && <p className="text-sm text-gray-500">{contact.role}</p>}
            {contact.company && (
              <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                <BuildingOfficeIcon className="h-4 w-4" />
                {contact.company}
              </p>
            )}
          </div>
        </div>

        {/* Deal value */}
        {contact.dealValue && contact.dealValue > 0 && (
          <div className="p-4 rounded-lg bg-green-50 border border-green-100">
            <p className="text-xs text-green-600 font-medium">Deal Value</p>
            <p className="text-2xl font-bold text-green-700">₩{contact.dealValue.toLocaleString()}</p>
          </div>
        )}

        {/* Contact info */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-gray-900">Contact Information</h3>
          {contact.email && (
            <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700">
              <EnvelopeIcon className="h-4 w-4" />
              {contact.email}
            </a>
          )}
          {contact.phone && (
            <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700">
              <PhoneIcon className="h-4 w-4" />
              {contact.phone}
            </a>
          )}
        </div>

        {/* Stage selection */}
        <div>
          <label className="label">Stage</label>
          <select
            value={contact.stage}
            onChange={(e) => handleStageChange(e.target.value as ContactStage)}
            className="select"
          >
            {[...PIPELINE_STAGES, 'churned' as ContactStage].map((stage) => (
              <option key={stage} value={stage}>{CONTACT_STAGE_CONFIG[stage].label}</option>
            ))}
          </select>
        </div>

        {/* Owner */}
        <div>
          <label className="label">Owner</label>
          {owner ? (
            <div className="flex items-center gap-2">
              <div className="avatar avatar-sm">{owner.name.charAt(0)}</div>
              <span className="text-sm text-gray-900">{owner.name}</span>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Unassigned</p>
          )}
        </div>

        {/* Notes */}
        {contact.notes && (
          <div>
            <label className="label">Notes</label>
            <p className="text-sm text-gray-600">{contact.notes}</p>
          </div>
        )}

        {/* Tags */}
        {contact.tags.length > 0 && (
          <div>
            <label className="label">Tags</label>
            <div className="flex flex-wrap gap-1">
              {contact.tags.map((tag) => (
                <span key={tag} className="tag">{tag}</span>
              ))}
            </div>
          </div>
        )}

        {/* Source */}
        {contact.source && (
          <div>
            <label className="label">Source</label>
            <p className="text-sm text-gray-900">{contact.source}</p>
          </div>
        )}

        <div className="pt-4 border-t border-gray-200 text-xs text-gray-400">
          <p>Created {format(new Date(contact.createdAt), 'MMM d, yyyy')}</p>
          {contact.lastContactedAt && (
            <p>Last contact {formatDistanceToNow(new Date(contact.lastContactedAt), { addSuffix: true })}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default CRMPage;
